"use client";

import { InterestGroup } from "../app/storyblok/[localeString]/support-hub/types/contact.types";
import { canonicalizeIds } from "./canonicalizeIds";
import {
  CATEGORY_QUERY_STRING_KEY,
  QUESTION_QUERY_STRING_KEY,
  SEARCH_QUERY_STRING_KEY,
  TOPIC_QUERY_STRING_KEY,
} from "./contactSettings";
import { notNullish } from "./notNullish";
import { useLocaleStringParam } from "./useLocaleStringParam";
import { useParams, usePathname, useSearchParams } from "next/navigation";
import { createContext, useContext } from "react";

/**
 * Hook that derives support-hub URL state (pathname, search params) and
 * provides URL builder functions for interest group, category, topic,
 * question, and search. Used by ContactPageRouterContextProvider.
 */
const useContactPageRouter = ({
  interestGroups,
}: {
  interestGroups: InterestGroup[];
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const params = useParams() ?? {};
  const localeString = useLocaleStringParam();

  const selectedInterestGroup = interestGroups.find(
    (item) => item.slug === params.interestGroupSlug,
  );

  const selectedTopicUuids = searchParams?.getAll(TOPIC_QUERY_STRING_KEY) ?? [];

  const selectedCategoryUuids =
    searchParams?.getAll(CATEGORY_QUERY_STRING_KEY) ?? [];

  const selectedQuestionUuid = searchParams?.get(QUESTION_QUERY_STRING_KEY);

  /**
   * Current search query from the URL (`q` param), trimmed, or null if not set.
   */
  const selectedSearchQuery =
    searchParams?.get(SEARCH_QUERY_STRING_KEY)?.trim() ?? null;

  /**
   * Returns a mutable copy of the current URL search params. Used when building
   * URLs that should preserve existing params (e.g. category, topic, question)
   * and only change search (q).
   */
  const copyParams = () => new URLSearchParams(searchParams?.toString() ?? "");

  /**
   * Builds pathname + query string, omitting the `?` when there are no params
   * (avoids a trailing `?` on the URL).
   */
  const pathnameWithParams = (p: URLSearchParams) => {
    const qs = p.toString();
    return qs ? `${pathname}?${qs}` : (pathname ?? "");
  };

  /**
   * Returns the URL for setting or clearing the search query. Preserves
   * current category and topic params. Opening search results clears the
   * question modal (`question` param). Empty query clears `q`.
   */
  const selectSearchUrl = ({ query }: { query: string }) => {
    const p = copyParams();
    if (query.trim()) {
      p.set(SEARCH_QUERY_STRING_KEY, query.trim());
      p.delete(QUESTION_QUERY_STRING_KEY);
    } else {
      p.delete(SEARCH_QUERY_STRING_KEY);
    }
    return pathnameWithParams(p);
  };

  /**
   * Returns the URL for clearing the search query. Preserves current category,
   * topic, and question params.
   */
  const deselectSearchUrl = () => {
    const p = copyParams();
    p.delete(SEARCH_QUERY_STRING_KEY);
    return pathnameWithParams(p);
  };

  /**
   * Returns the URL for the support-hub home (no interest group selected).
   */
  const deselectInterestGroupUrl = () => {
    return `/${localeString}/support-hub`;
  };

  /**
   * Returns the URL for a specific interest group by slug. No category, topic,
   * question, or search params.
   */
  const selectInterestGroupUrl = ({ slug }: { slug: string }) => {
    return `/${localeString}/support-hub/${slug}`;
  };

  /**
   * Returns the URL for toggling an interest group: if it is already selected,
   * goes to support-hub home; otherwise selects it.
   *
   * Special case: while a search is active (`?q=...`), the path's interest
   * group is presented as "deselected" in the UI (see `InterestsGroups`),
   * because search results supersede the interest-group view. In that state
   * clicking the path's slug must "re-select" it (clearing search) rather
   * than deselect it — so we always route to `selectInterestGroupUrl` while
   * search is active, which drops `q` and lands on the interest-group page.
   */
  const toggleInterestGroupUrl = ({ slug }: { slug: string }) => {
    if (selectedSearchQuery) {
      return selectInterestGroupUrl({ slug });
    }
    return selectedInterestGroup && selectedInterestGroup.slug === slug
      ? deselectInterestGroupUrl()
      : selectInterestGroupUrl({ slug });
  };

  /**
   * Returns the URL for adding a category to the selection. Preserves
   * current categories and topics. Does not preserve search (q) or question.
   *
   * Category/topic UUIDs are canonicalized (deduped + sorted) before being
   * written to the URL, rather than left in click order, so that two users
   * who end up with the same *set* of filters selected -- regardless of the
   * order they clicked them in -- land on the same URL. This matters because
   * these are real pages Akamai edge-caches by exact URL; click-order
   * variation would otherwise fragment the cache across many equivalent URLs.
   */
  const selectCategoryUrl = ({ uuid }: { uuid: string }) => {
    const newSearchParams = new URLSearchParams();

    /**
     * Add newly selected category, and preserve the already-selected
     * categories.
     */
    for (const selectedCategoryUuid of canonicalizeIds([
      ...selectedCategoryUuids,
      uuid,
    ])) {
      newSearchParams.append(CATEGORY_QUERY_STRING_KEY, selectedCategoryUuid);
    }
    /**
     * Preserve already-selected topics.
     */
    for (const selectedTopicUuid of canonicalizeIds(selectedTopicUuids)) {
      newSearchParams.append(TOPIC_QUERY_STRING_KEY, selectedTopicUuid);
    }

    return `${pathname}?${newSearchParams.toString()}`;
  };

  /**
   * Returns the URL for removing a category from the selection. Keeps other
   * categories and only topics that belong to the remaining categories. Does
   * not preserve search (q) or question.
   */
  const deselectCategoryUrl = ({ uuid }: { uuid: string }) => {
    const newSearchParams = new URLSearchParams();

    /**
     * De-select category, preserve other selected categories.
     */
    const newSelectedCategories =
      selectedInterestGroup?.categories
        .filter((item) => selectedCategoryUuids.includes(item.uuid))
        .filter((item) => item.uuid !== uuid) ?? [];

    /**
     * De-select selected topics which are not associated with any
     * selected categories. Preserve selected topics otherwise.
     */
    const newAvailableTopicUuids = newSelectedCategories
      .flatMap((item) => item.topics)
      .filter(notNullish)
      .map((item) => item?.uuid);

    const newSelectedTopicUuids = selectedTopicUuids.filter((topicUuid) =>
      newAvailableTopicUuids.includes(topicUuid),
    );

    for (const selectedCategoryUuid of canonicalizeIds(
      newSelectedCategories.map((item) => item.uuid),
    )) {
      newSearchParams.append(CATEGORY_QUERY_STRING_KEY, selectedCategoryUuid);
    }
    for (const topicUuid of canonicalizeIds(newSelectedTopicUuids)) {
      newSearchParams.append(TOPIC_QUERY_STRING_KEY, topicUuid);
    }

    return `${pathname}?${newSearchParams.toString()}`;
  };

  /**
   * Returns the URL for clearing all category (and topic) selection, i.e.
   * back to the interest group page with no category/topic params.
   */
  const deselectAllCategoriesUrl = () => pathname ?? "";

  /**
   * Returns the URL for toggling a category: if already selected, deselects it;
   * otherwise adds it to the selection.
   */
  const toggleCategoryUrl = ({ uuid }: { uuid: string }) => {
    return selectedCategoryUuids.includes(uuid)
      ? deselectCategoryUrl({ uuid })
      : selectCategoryUrl({ uuid });
  };

  /**
   * Returns the URL for adding a topic to the selection. Preserves current
   * categories and topics. Does not preserve search (q) or question.
   */
  const selectedTopicUrl = ({ uuid }: { uuid: string }) => {
    const newSearchParams = new URLSearchParams();

    /**
     * Select topic, preserve already-selected topics.
     */
    for (const topicUuid of canonicalizeIds([...selectedTopicUuids, uuid])) {
      newSearchParams.append(TOPIC_QUERY_STRING_KEY, topicUuid);
    }

    /**
     * Preserve selected categories.
     */
    for (const categoryUuid of canonicalizeIds(selectedCategoryUuids)) {
      newSearchParams.append(CATEGORY_QUERY_STRING_KEY, categoryUuid);
    }

    return `${pathname}?${newSearchParams.toString()}`;
  };

  /**
   * Returns the URL for removing a topic from the selection. Preserves
   * current categories and other topics. Does not preserve search (q) or
   * question.
   */
  const deselectTopicUrl = ({ uuid }: { uuid: string }) => {
    const newSearchParams = new URLSearchParams();

    /**
     * De-select topic, preserve already-selected topics otherwise.
     */
    for (const topicUuid of canonicalizeIds(
      selectedTopicUuids.filter((item) => item !== uuid),
    )) {
      newSearchParams.append(TOPIC_QUERY_STRING_KEY, topicUuid);
    }

    /**
     * Preserve selected categories.
     */
    for (const categoryUuid of canonicalizeIds(selectedCategoryUuids)) {
      newSearchParams.append(CATEGORY_QUERY_STRING_KEY, categoryUuid);
    }

    return `${pathname}?${newSearchParams.toString()}`;
  };

  /**
   * Returns the URL for "deselect all topics": keeps the current category
   * params but removes all topic params. Search (q) and question modal state
   * are not preserved (consistent with other category/topic URL changes).
   */
  const deselectAllTopicsUrl = () => {
    const newSearchParams = new URLSearchParams();
    for (const categoryUuid of canonicalizeIds(selectedCategoryUuids)) {
      newSearchParams.append(CATEGORY_QUERY_STRING_KEY, categoryUuid);
    }
    const qs = newSearchParams.toString();
    return qs ? `${pathname}?${qs}` : (pathname ?? "");
  };

  /**
   * Returns the URL for toggling a topic: if already selected, deselects it;
   * otherwise adds it to the selection.
   */
  const toggleTopicUrl = ({ uuid }: { uuid: string }) => {
    return selectedTopicUuids.includes(uuid)
      ? deselectTopicUrl({ uuid })
      : selectedTopicUrl({ uuid });
  };

  /**
   * Returns the URL for opening a question in the modal. Preserves current
   * category, topic, and search (q) params.
   */
  const selectQuestionUrl = ({ uuid }: { uuid: string }) => {
    const newSearchParams = new URLSearchParams(searchParams?.toString());

    newSearchParams.set(QUESTION_QUERY_STRING_KEY, uuid);

    return `${pathname}?${newSearchParams.toString()}`;
  };

  /**
   * Returns the URL for closing the question modal. Preserves current
   * category, topic, and search (q) params.
   */
  const deselectQuestionUrl = () => {
    const newSearchParams = new URLSearchParams(searchParams?.toString());

    newSearchParams.delete(QUESTION_QUERY_STRING_KEY);

    return `${pathname}?${newSearchParams.toString()}`;
  };

  return {
    interestGroups,
    selectedCategoryUuids,
    selectedTopicUuids,
    selectedQuestionUuid,
    selectedSearchQuery,
    selectSearchUrl,
    deselectSearchUrl,
    toggleInterestGroupUrl,
    selectInterestGroupUrl,
    deselectInterestGroupUrl,
    toggleCategoryUrl,
    selectCategoryUrl,
    deselectCategoryUrl,
    deselectAllCategoriesUrl,
    selectedTopicUrl,
    deselectTopicUrl,
    deselectAllTopicsUrl,
    toggleTopicUrl,
    selectQuestionUrl,
    deselectQuestionUrl,
  };
};

const ContactPageRouterContext = createContext<
  ReturnType<typeof useContactPageRouter> | undefined
>(undefined);

/**
 * Returns the contact page router context (URL state and URL builder
 * functions). Must be used within ContactPageRouterContextProvider.
 */
export const useContactPageRouterContext = () => {
  const context = useContext(ContactPageRouterContext);

  if (!context) {
    throw new Error("useContactPageRouterContext was used outside of Provider");
  }

  return context;
};

/**
 * Provider that supplies support-hub URL state and URL builders to the tree.
 * Requires interest groups so that selected interest group can be derived
 * from the current path.
 */
export const ContactPageRouterContextProvider = ({
  interestGroups,
  children,
}: {
  interestGroups: InterestGroup[];
  children: React.ReactNode;
}) => {
  const contactPageRouter = useContactPageRouter({ interestGroups });
  return (
    <ContactPageRouterContext.Provider value={contactPageRouter}>
      {children}
    </ContactPageRouterContext.Provider>
  );
};
